
// frontend/app/exhibit/v2/models/teachClassifier.js
// Nick/Kanchan "Teach a Machine" classifier.
//
// Two input features:
//   b = blueness
//   r = roundness
//
// Output:
//   probability of "circle"
//
// This intentionally reproduces the learning behavior from the
// reference prototype.

export const TEACH_STEPS = 30;
export const TEACH_LEARNING_RATE = 2;

export const TEACH_QUEUE = [
  { b: 0.95, r: 0.70, y: 1 },
  { b: 0.10, r: 0.30, y: 0 },
  { b: 0.90, r: 0.60, y: 1 },
  { b: 0.15, r: 0.20, y: 0 },
  { b: 0.85, r: 0.75, y: 1 },
  { b: 0.20, r: 0.35, y: 0 },

  // Shape 7 — blue triangle.
  // The color shortcut should break here.
  { b: 0.92, r: 0.10, y: 0, surprise: true },

  // Shape 8 — red circle.
  // Same shortcut, reversed.
  { b: 0.12, r: 0.72, y: 1, reverse: true },

  { b: 0.88, r: 0.15, y: 0 },
  { b: 0.18, r: 0.68, y: 1 },

  // Shape 11 — blue triangle test.
  { b: 0.90, r: 0.12, y: 0, test: true },

  { b: 0.85, r: 0.85, y: 1 },
];

export function createTeachModel() {
  return {
    wBlue: 0,
    wRound: 0,
    bias: 0,
  };
}

function sigmoid(value) {
  return 1 / (1 + Math.exp(-value));
}

export function teachProbability(model, example) {
  return sigmoid(
    model.wBlue * example.b +
      model.wRound * example.r +
      model.bias
  );
}

export function trainTeachModel(
  currentModel,
  examples,
  steps = TEACH_STEPS,
  learningRate = TEACH_LEARNING_RATE
) {
  if (!examples.length) {
    return { ...currentModel };
  }

  let {
    wBlue,
    wRound,
    bias,
  } = currentModel;

  for (let step = 0; step < steps; step += 1) {
    let gradBlue = 0;
    let gradRound = 0;
    let gradBias = 0;

    for (const example of examples) {
      const probability = sigmoid(
        wBlue * example.b +
          wRound * example.r +
          bias
      );

      const error = probability - example.y;

      gradBlue += error * example.b;
      gradRound += error * example.r;
      gradBias += error;
    }

    const count = examples.length;

    wBlue -= learningRate * (gradBlue / count);
    wRound -= learningRate * (gradRound / count);
    bias -= learningRate * (gradBias / count);
  }

  return {
    wBlue,
    wRound,
    bias,
  };
}

export function teachLoss(model, examples) {
  if (!examples.length) {
    return 0;
  }

  const total = examples.reduce((sum, example) => {
    const rawProbability = teachProbability(model, example);

    const probability = Math.min(
      Math.max(rawProbability, 0.000001),
      0.999999
    );

    return (
      sum -
      (example.y
        ? Math.log(probability)
        : Math.log(1 - probability))
    );
  }, 0);

  return total / examples.length;
}
